import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UIRouter } from '@uirouter/angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NavigationService } from '../../../Layout/navigation.service';
import { NubService } from '../../../Navigation/Nub/nub.service';
import { TimerService } from '../../../UI-Lib/Utilities';
import { SearchService } from '../../search.service';
import { QuickCriteria, QuickCriteriaFilter } from '../Criteria/quickCriteria.model';

@Component({
    selector: 'quick-search-result',
    templateUrl: './quickSearchResult.component.html',
    styleUrls: ['./quickSearchResult.component.scss'],
    standalone: false,
})
export class QuickSearchResultComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _searchService: SearchService,
        private readonly _navigationService: NavigationService,
        private readonly _router: UIRouter,
        private readonly _nubService: NubService,
        private readonly _timer: TimerService,
    ) {}
    @Output() closeSearchPanel = new EventEmitter<void>();

    loading = false;
    allowToShowSpinner = true; // we can hide spinner if it's unlikely that there are more results
    itemsPerPage = 30;
    results: any[] = [];
    criteria: QuickCriteria;
    extraFilters: QuickCriteriaFilter[] = [];

    private _destroy$: Subject<void> = new Subject();

    async ngOnInit(): Promise<void> {
        await this.getResults();
        this.newResults();

        this._searchService.criteriaChanged$
            .pipe(takeUntil(this._destroy$))
            .subscribe(this.criteriaChangedListenerInfinite.bind(this));
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    newResults(): void {
        this._searchService.focusOnResult(0);

        if (this.results.length === 1) {
            this._timer.setTimeout(() => {
                this.navigate(this.results[0]);
            }, 1000);
        }
    }

    closeSearch(): void {
        this.closeSearchPanel.emit();
        this._nubService.showQuickSearchNub();
    }

    async criteriaChangedListenerInfinite(): Promise<void> {
        this.allowToShowSpinner = true;

        this.resetResults();

        await this.getResults();
        this.newResults();
    }

    async getResults(): Promise<void> {
        this.loading = true;

        try {
            this.criteria = this._searchService.getCriteria();
            this.loadExtraFilters();

            const pageNumber = this.results ? Math.ceil(this.results.length / this.itemsPerPage) + 1 : 1;

            const resultsObj = await this._searchService.searchElastic(pageNumber, this.itemsPerPage);

            if (resultsObj.results.length === 0) this.allowToShowSpinner = false;

            this.results = this.results.concat(resultsObj.results);
        } finally {
            this.loading = false;
        }
    }

    navigate(result, index?: number, e?: Event): void {
        switch (this.criteria.resultType) {
            case 'parcel':
                this._router.stateService.go('parcel', {
                    companyId: result.companyID,
                    siteId: result.siteID,
                    parcelId: result.parcelID,
                });
                break;
            case 'site':
                this._router.stateService.go('site', { companyId: result.companyID, siteId: result.siteID });
                break;
            case 'company':
                this._router.stateService.go('company', { companyId: result.companyID });
                break;
        }

        this._searchService.setClickedOnResultIndex(index);
        if (
            ['navbar', 'hotkey', 'home', 'dashboard', 'quick-search-nub'].includes(
                this._navigationService.quickSearchInitiator,
            )
        ) {
            this._navigationService.quickSearchInitiator = null;
        }
        this.closeSearch();

        if (e) {
            e.stopPropagation();
        }
    }

    getResultUrl(result): string {
        const baseUrl = '/company/';
        switch (this.criteria.resultType) {
            case 'parcel':
                return `${baseUrl + result.companyID}/site/${result.siteID}/parcel/${result.parcelID}`;
            case 'site':
                return `${baseUrl + result.companyID}/site/${result.siteID}`;
            case 'company':
                return baseUrl + result.companyID;
        }
    }

    loadExtraFilters(): void {
        const fieldsToReject = ['parcelPropType', 'parcelAcctNum'];

        this.criteria.activeFilters.filter((x) => !!x.text || !x.default || !fieldsToReject.includes(x.field));
    }

    resetResults(): void {
        this.results = [];
    }

    async refreshResults(): Promise<void> {
        this.results = [];
        await this.getResults();
        this.newResults();
    }

    pgDn(e: Event, index: number): void {
        let focused = index + 10;
        const lastIndex = this.results.length - 1;

        focused = focused > lastIndex ? lastIndex : focused;

        this._searchService.focusOnResult(focused);
        e.preventDefault();
    }

    pgUp(e: Event, index: number): void {
        let focused = index - 10;
        focused = focused < 0 ? 0 : focused;

        this._searchService.focusOnResult(focused);
        e.preventDefault();
    }

    keyup($event: KeyboardEvent, index: number): void {
        if ($event.code === '34') {
            this.pgDn($event, index);
        } else if ($event.code === '33') {
            this.pgUp($event, index);
        }
    }

    hoverIn(index: number): void {
        this._searchService.focusOnResult(index);
    }
}
